import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';

interface PaymentMatch {
  paymentQuickbooksId: string;
  paymentAmount: number;
  paymentDate: string;
  paymentCustomerName?: string;
  paymentMethod?: string;
  paymentReference?: string;
}

interface EmployeeOutreachDialogProps {
  open: boolean;
  payment: PaymentMatch;
  onClose: () => void;
  onConfirm: (employeeId: string, employeeName: string, employeeEmail: string, question: string, sendEmail: boolean) => Promise<void>;
}

// Mock employee list - in production this would come from an API
const EMPLOYEES = [
  { id: '1', name: 'Sarah Martinez', email: 'sarah.martinez@barretthtl.com' },
  { id: '2', name: 'John Smith', email: 'john.smith@barretthtl.com' },
  { id: '3', name: 'Emily Johnson', email: 'emily.johnson@barretthtl.com' },
  { id: '4', name: 'Michael Brown', email: 'michael.brown@barretthtl.com' },
  { id: '5', name: 'Jennifer Davis', email: 'jennifer.davis@barretthtl.com' },
];

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const EmployeeOutreachDialog: React.FC<EmployeeOutreachDialogProps> = ({
  open,
  payment,
  onClose,
  onConfirm,
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState<typeof EMPLOYEES[0] | null>(null);
  const [question, setQuestion] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate default question based on payment info
  React.useEffect(() => {
    if (open && payment) {
      const defaultQuestion = payment.paymentReference
        ? `Do you know which client might have sent a ${formatCurrency(payment.paymentAmount)} ${payment.paymentMethod || 'payment'} on ${formatDate(payment.paymentDate)}? The reference was "${payment.paymentReference}".`
        : `Do you know which client might have sent a ${formatCurrency(payment.paymentAmount)} ${payment.paymentMethod || 'payment'} on ${formatDate(payment.paymentDate)}?`;
      setQuestion(defaultQuestion);
    }
  }, [open, payment]);

  const handleConfirm = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee');
      return;
    }
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onConfirm(selectedEmployee.id, selectedEmployee.name, selectedEmployee.email, question, sendEmail);
      // Reset form
      setSelectedEmployee(null);
      setQuestion('');
      setSendEmail(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record outreach');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedEmployee(null);
      setQuestion('');
      setSendEmail(true);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ask Employee About This Payment</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Payment Details:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {formatCurrency(payment.paymentAmount)} - {payment.paymentCustomerName || 'Unknown Customer'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {payment.paymentMethod || 'Unknown Method'} on {formatDate(payment.paymentDate)}
            {payment.paymentReference && ` | Ref: ${payment.paymentReference}`}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Autocomplete
          options={EMPLOYEES}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          value={selectedEmployee}
          onChange={(_, newValue) => setSelectedEmployee(newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Employee" placeholder="Select an employee to ask" />
          )}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Question / Notes"
          multiline
          rows={4}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          placeholder="Enter your question for the employee..."
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          }
          label="Send email notification to employee"
        />

        {!sendEmail && (
          <Alert severity="info" sx={{ mt: 1 }}>
            The outreach will be recorded but no email will be sent. You'll need to contact the employee directly.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !selectedEmployee || !question.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {sendEmail ? 'Send & Record' : 'Record Outreach'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeOutreachDialog;
